import {ModifyString, SleepAsync, WaitXThenRun} from "js-vextensions";
import {AssertNotify, TextSpeaker} from "web-vcore";
import {store} from "../../Store/index.js";
import {InAndroid, nativeBridge} from "../Bridge/Bridge_Native.js";
import type {EngineSessionComp} from "../../Engine/LiveSession/Components/EngineSessionComp.js";
import type {DreamRecallComp} from "../../Engine/LiveSession/Components/DreamRecallComp.js";
import {GetUserEntityTags, MeID} from "../../Server/DB/users.js";
import {Sound, SoundType} from "../../Server/DB/sounds/@Sound.js";
import {LiveSession} from "../../Engine/LiveSession.js";
import {TTSVoiceType} from "../../Server/DB/engineConfigs/@EngineConfig/@EC_Visualization.js";

export function StandardizeTextForTTS(text: string, convertDigitsToWords: boolean, addMissingSpaces: boolean): string {
	let newText = text;
	if (convertDigitsToWords) {
		newText = ConvertDigitsToWords(newText);
	}
	if (addMissingSpaces) {
		// Examples:
		// 1Kitchen -> 1 Kitchen
		// 2UpBath -> 2 Up Bath
		// (this helps Whisper.ts notice that its "recognized text" matches recently "spoken text", so it can exclude it)
		newText = newText.replace(/([a-z0-9])([A-Z])/g, (m, sub1, sub2)=>`${sub1} ${sub2}`);
	}
	return newText;
}

// Converts digit characters to word form (e.g., "1" -> "One", "2" -> "Two")
export function ConvertDigitsToWords(text: string): string {
	const digitMap: {[key: string]: string} = {
		"0": "Zero",
		"1": "One",
		"2": "Two",
		"3": "Three",
		"4": "Four",
		"5": "Five",
		"6": "Six",
		"7": "Seven",
		"8": "Eight",
		"9": "Nine",
	};
	return text.replace(/\d/g, digit=>digitMap[digit] ?? digit);
}

export function NarrateText_StopAll() {
	if (g.speechSynthesis != null) {
		g.speechSynthesis.cancel();
	}
	if (InAndroid(0)) {
		nativeBridge.Call("StopSpeaking");
	}
}

export type NarrateText_ForEngineComp_OptInit = Omit<NarrateText_ForSession_OptInit, "text">;
export async function NarrateText_ForEngineComp(comp: EngineSessionComp, text: string, volumeMultiplier_preGlobalMultiplier: number, voiceSoundTagFromComp?: string|n, coreOpt?: NarrateText_ForEngineComp_OptInit) {
	return await NarrateText_ForSession({
		session: comp.s,
		volumeMultiplier_preGlobalMultiplier,
		voiceSoundTagFromComp,
	}, {
		...coreOpt,
		text,
	});
}

export type NarrateText_ForSession_OptInit = Omit<NarrateTextOptionsInit, "volumeMultiplier">;
export async function NarrateText_ForSession(extraOpt: {
	session: LiveSession,
	volumeMultiplier_preGlobalMultiplier: number,
	voiceSoundTagFromComp?: string|n,
}, coreOpt: Omit<NarrateTextOptionsInit, "volumeMultiplier">) {
	return await NarrateText({
		...coreOpt,
		sound: coreOpt.sound ?? (()=>{
			const dreamRecallComp = extraOpt.session.Comp_ByString("DreamRecallComp") as DreamRecallComp;
			const findSoundByTag = (tag: string|n)=>{
				if (tag == null || tag.trim().length == 0) return null;
				return dreamRecallComp.sounds_all.filter(a=>GetUserEntityTags(MeID(), "sounds", a.id)?.includes(tag)).Random();
			};
			return findSoundByTag(extraOpt.voiceSoundTagFromComp) ?? findSoundByTag(extraOpt.session.c.general.defaultVoice_soundTag);
		})() ?? undefined,
		volumeMultiplier: extraOpt.volumeMultiplier_preGlobalMultiplier * extraOpt.session.GetSettingValue(c=>c.general.globalVolumeMultiplier),
	});
}

export type NarrateTextOptionsInit = ConstructorParameters<typeof NarrateTextOptions>[0];
// todo: update this to be like PlaySoundOpts, and have two struct versions: NarrateTextOptions, and NarrateTextOptions_Finalized
export class NarrateTextOptions {
	constructor(data: RequiredBy<Partial<NarrateTextOptions>, "text">) {
		Object.assign(this, data);
		this.Finalize();
	}

	text: string;
	textSpeaker = NarrateText_sharedTextSpeaker;

	volumeMultiplier = 1;
	speedMultiplier = 1;
	sound: Sound;
	utteranceId: string; // if not specified, will be generated automatically
	onUtteranceStart?: ()=>void;
	onUtteranceInterrupted_effect?: "resolve" | "reject";

	saveToFile_relFilePath?: string; // if specified, the spoken audio will be saved to a file at this path (absolute-ish, through media-store); only supported on Android currently

	_finalVolume: number;
	_finalSpeed: number;
	_finalVolume_asEnergy: number;
	finalize_lastStep: ((self: NarrateTextOptions)=>void)|n;
	Finalize() {
		this.utteranceId ??= NewUtteranceID(this.text);
		NarrateText_lastUtteranceId = this.utteranceId;
		if (this.sound == null) {
			this.sound = new Sound({
				type: SoundType.Speech,
				pitch: 1,
				volume: 1,
				speed: 1,
			});
			// on android, slow down (the fallback) voice speed by a lot (android's TTS engine speaks very fast at speed=1)
			/*if (InAndroid(0)) {
				sound.speed = .6;
			} else {
				// on web-api, slow it down some as well (speed=1 is not as fast as android's TTS engine, but still faster than generally desired)
				sound.speed = .9;
			}*/
		}

		this._finalVolume = this.sound.volume * this.volumeMultiplier;
		this._finalSpeed = this.sound.speed * this.speedMultiplier * store.main.settings.audio.voiceSpeedMultiplier;
		// var named this way, on the assumption the api's "volume" represents energy (eg. loudness^2) rather than perceptual-loudness
		this._finalVolume_asEnergy = Math.pow(this._finalVolume, store.main.settings.audio.loudnessCurve_systemTTS);
		this.finalize_lastStep?.(this);
	}
}
export const NarrateText_sharedTextSpeaker = new TextSpeaker();

export let NarrateText_lastUtteranceId: string|n; // track last utterance-id, so that callers can check if their utterance is still the last one sent (thus knowing it's safe to call NarrateText_StopAll)
export async function NarrateText(options: NarrateTextOptionsInit) {
	const opt = new NarrateTextOptions(options);
	// cancel voicing if volume is 0 or text is empty; this way, "silent" voicings don't interrupt audible ones
	if (opt.volumeMultiplier == 0 || opt.text == null || opt.text == "") return;
	if (opt.saveToFile_relFilePath != null && !InAndroid(0)) return void AssertNotify(false, "NarrateText: TTS save-to-file option only supported on Android currently.");
	opt.Finalize();

	// if web-api for speech-synthesis is supported (and has the featured needed), use it
	if (g.speechSynthesis != null && !opt.saveToFile_relFilePath) {
		try {
			await opt.textSpeaker.Speak({text: opt.text, voice: opt.sound.voice, volume: opt._finalVolume_asEnergy, rate: opt._finalSpeed, pitch: opt.sound.pitch});
		} catch (ex) {
			// don't show an error-message for these normal/expected cases (at least the "canceled" event is normal/expected)
			if (ex instanceof SpeechSynthesisErrorEvent && (ex.error == "canceled" || ex.error == "interrupted")) return;
			throw ex;
		}
	}
	// else, if on Android, use Android's TTS
	else if (InAndroid(0)) {
		nativeBridge.Call("SpeakText", {
			text: opt.text, volume: opt._finalVolume_asEnergy, speed: opt._finalSpeed, utteranceId: opt.utteranceId,
			relFilePath: opt.saveToFile_relFilePath,
		});

		let promiseFuncs: {resolve: ()=>void, reject: ()=>void}|n;
		const meta = new UtteranceMeta({
			id: opt.utteranceId,
			text: opt.text,
			onStart: opt?.onUtteranceStart,
			// We early-set these handlers, so if they get called synchronously (prior to the promise below being constructed), the promise still ends up able to resolve/reject based on the call.
			// (This can happen, for example, if an OnUtteranceStart event comes in prior to )
			onDone: async()=>{
				while (promiseFuncs == null) { await SleepAsync(10); }
				promiseFuncs.resolve();
			},
			onError: async()=>{
				while (promiseFuncs == null) { await SleepAsync(10); }
				promiseFuncs.reject();
			},
			onInterrupted: async()=>{
				while (promiseFuncs == null) { await SleepAsync(10); }
				// When interrupted, default to resolving the promise. (Some callers may want an error, others a [quick returning] success -- but I think the latter is more common.)
				if (opt?.onUtteranceInterrupted_effect == "reject") {
					promiseFuncs.reject();
				} else {
					promiseFuncs.resolve();
				}
			},
		});
		utteranceMetas.set(opt.utteranceId, meta);

		// defensive; after 60s, if the utterance has not reached an end, assume it's stuck and mark it as interrupted
		WaitXThenRun(60 * 1000, ()=>{
			if (meta.doneTime == null && meta.errorTime == null && meta.interruptedTime == null) {
				meta.MarkInterrupted();
				AssertNotify(false, `TTS utterance seemingly stuck after 60s; marking as interrupted. @id:${opt.utteranceId} @text:${opt.text}`);
			}
		});

		return new Promise<void>((resolve, reject)=>{
			promiseFuncs = {resolve, reject};
		});
	}
}

export function NewUtteranceID(text: string, startTime = Date.now()) {
	const hashCode = s=>s.split("").reduce((a,b)=>{a=((a<<5)-a)+b.charCodeAt(0);return a&a},0);
	return `${startTime}_${hashCode(text)}`;
}

class UtteranceMeta {
	constructor(data: RequiredBy<Partial<UtteranceMeta>, "id" | "text">) { Object.assign(this, data); }

	id: string;
	text: string;
	onStart?: ()=>void;
	onDone?: ()=>void;
	onError?: ()=>void;
	onInterrupted?: ()=>void; // custom (info-signaling workaround, for Android TTS not calling onDone/onError for earlier utterances when a new one is started/replaces-them)

	creationTime = Date.now();
	startTime?: number;
	doneTime?: number;
	errorTime?: number;
	interruptedTime?: number; // custom

	MarkStart(timestamp = Date.now()) {
		this.startTime = timestamp;
		this.onStart?.();

		// Issue: The Android TTS engine (at least how I currently use it) has new utterances simply replace any existing utterances, but WITHOUT those earlier utterances getting their onDone or onError event called.
		// Fix: When onUtteranceStart occurs for a new utterance, find the metadata we have stored for any earlier utterances, and mark their interruptedTime as now. (clarifying that the lack of a doneTime/errorTime doesn't mean its still running)
		const utteranceList = Array.from(utteranceMetas.values());
		const selfIndex = utteranceList.indexOf(this);
		if (selfIndex != -1) {
			const earlierUtterances = utteranceList.slice(0, selfIndex);
			for (const otherMeta of earlierUtterances) {
				// if utterance "looks like" its still running (but we know it's not, since we're the newest utterance to start), mark it as interrupted
				if (/*otherMeta.startTime != null &&*/ otherMeta.doneTime == null && otherMeta.errorTime == null && otherMeta.interruptedTime == null) {
					otherMeta.MarkInterrupted(timestamp);
				}
			}
		}
	}
	MarkDone(timestamp = Date.now()) {
		this.doneTime = timestamp;
		this.onDone?.();
		DeleteUtteranceMetaOnceSafe(this.id);
	}
	MarkError(timestamp = Date.now()) {
		this.errorTime = timestamp;
		this.onError?.();
		DeleteUtteranceMetaOnceSafe(this.id);
	}
	MarkInterrupted(timestamp = Date.now()) {
		this.interruptedTime = timestamp;
		this.onInterrupted?.();
		DeleteUtteranceMetaOnceSafe(this.id);
	}
}

export const utteranceMetas = new Map<string, UtteranceMeta>();
function DeleteUtteranceMetaOnceSafe(utteranceID: string) {
	// To be safe, wait 30+10 seconds after avoid-transcribe-buffer logic is done with this utterance, then delete the entry for it.
	// * 30s: How long the audio-recorder buffer can get before sending audio for whisper-transcription.
	// * 15s: Extra padding, to account for other possible delays. (eg. time for whisper-api to processing the audio chunk)
	WaitXThenRun((store.main.settings.transcribe.voice_avoidTranscribePadding * 1000) + 45000, ()=>{
		utteranceMetas.delete(utteranceID);
	});
}

nativeBridge.RegisterFunction("OnUtteranceStart", (utteranceID: string)=>{
	utteranceMetas.get(utteranceID)?.MarkStart();
});
nativeBridge.RegisterFunction("OnUtteranceDone", (utteranceID: string)=>{
	utteranceMetas.get(utteranceID)?.MarkDone();
});
nativeBridge.RegisterFunction("OnUtteranceError", (utteranceID: string)=>{
	utteranceMetas.get(utteranceID)?.MarkError();
});

// utterance-meta register/unregister from external/non-tts sources
// ==========

/*export function NotifyNonTTSUtteranceRange_FromSoundFile(filePath: string, wordsIgnoreGroup: number|n, startTime: number, endTime = Date.now()) {
	if (wordsIgnoreGroup == null) return null;
	const utteranceID = NotifyNonTTSUtteranceStart_FromSoundFile(filePath, wordsIgnoreGroup, startTime);
	return NotifyNonTTSUtteranceEnd(utteranceID!, endTime);
}*/
export function NotifyNonTTSUtteranceStart_FromSoundFile(filePath: string, wordsIgnoreGroup: number|n, startTime = Date.now()) {
	if (wordsIgnoreGroup == null) return null;
	const wordsIgnoreTextFromFilePath = filePath.replace(/\.[^._]$/, "").split("_")[wordsIgnoreGroup - 1] as string|n;
	const textToIgnore_normalized = wordsIgnoreTextFromFilePath == null ? "" : ModifyString(wordsIgnoreTextFromFilePath, m=>[m.lowerUpper_to_lowerSpaceLower]);
	return NotifyNonTTSUtteranceStart(textToIgnore_normalized, startTime);
}

export function NotifyNonTTSUtteranceStart(text: string, startTime = Date.now()) {
	const utteranceID = NewUtteranceID(text, startTime);
	const meta = new UtteranceMeta({id: utteranceID, text});
	utteranceMetas.set(utteranceID, meta);
	meta.MarkStart(startTime);
	return utteranceID;
}
export function NotifyNonTTSUtteranceEnd(id: string, endTime = Date.now()) {
	utteranceMetas.get(id)?.MarkDone(endTime);
}